import React, { useState, useEffect } from 'react';
import { Receipt, Paperclip, Camera, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { expenseService } from '@/services/expenseService';
import { arrivalService } from '@/services/arrivalService';
import { farmService } from '@/services/farmService';
import { cycleService } from '@/services/cycleService';
import {
  TransactionCategoryRecord,
  SupplierRecord,
  FarmRecord,
  ProductionCycleRecord,
} from '@/types/database';

interface CycleExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (expense: any) => void;
  cycleId?: string | null;
  cycleCode?: string;
  farmId?: string | null;
}

export const CycleExpenseModal: React.FC<CycleExpenseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  cycleId,
  cycleCode,
  farmId,
}) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedCycleId, setSelectedCycleId] = useState<string>(cycleId || '');
  const [selectedFarmId, setSelectedFarmId] = useState<string>(farmId || '');
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');

  const [expenseDate, setExpenseDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState<number | ''>('');
  const [description, setDescription] = useState<string>('');
  const [referenceNo, setReferenceNo] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  const [categories, setCategories] = useState<TransactionCategoryRecord[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);
  const [farms, setFarms] = useState<FarmRecord[]>([]);
  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (cycleId) setSelectedCycleId(cycleId);
    if (farmId) setSelectedFarmId(farmId);
  }, [cycleId, farmId]);

  useEffect(() => {
    if (!isOpen) return;
    setErrorMsg(null);

    Promise.all([
      expenseService.getTransactionCategories('expense'),
      arrivalService.getSuppliers(),
      farmService.getActiveFarms(),
      cycleService.getCycles({ status: 'active' }),
    ]).then(([cats, supps, fms, cycs]) => {
      setCategories(cats);
      setSuppliers(supps);
      setFarms(fms);
      setCycles(cycs);

      if (cats.length > 0 && !selectedCategoryId) {
        setSelectedCategoryId(cats[0].id);
      }
    });
  }, [isOpen]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachedFile(e.target.files[0]);
    }
  };

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!selectedCategoryId) {
      setErrorMsg('Please select a Transaction Category.');
      return;
    }
    if (!expenseDate) {
      setErrorMsg('Expense Date is required.');
      return;
    }
    if (amount === '' || Number(amount) < 0) {
      setErrorMsg('Amount must be 0 or positive.');
      return;
    }
    if (!description.trim()) {
      setErrorMsg('Expense description is required.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        category_id: selectedCategoryId,
        cycle_id: selectedCycleId || null,
        farm_id: selectedFarmId || null,
        supplier_id: selectedSupplierId || null,
        expense_date: expenseDate,
        amount: Number(amount),
        account_code: selectedCategory?.code || null,
        description: description.trim(),
        reference_no: referenceNo.trim() || null,
        notes: notes.trim() || null,
      };

      const result = await expenseService.createExpense(payload, attachedFile);
      onSuccess(result);
      onClose();
    } catch (err: any) {
      console.error('Error recording expense:', err);
      setErrorMsg(err.message || 'Failed to record expense.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] flex flex-col p-4 sm:p-6 overflow-hidden">
        <DialogHeader className="pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-800 flex items-center justify-center">
              <Receipt className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-bold text-slate-900">
                Log Operating Expense
              </DialogTitle>
              <p className="text-xs text-slate-500">
                Itemized operating expense linked to Chart of Accounts (COA).
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto py-3 space-y-4 no-scrollbar">
          {errorMsg && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Allocation: Destination Cycle or Farm Location */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Bound Cycle (Optional)</label>
              {cycleCode ? (
                <div className="p-2 rounded-md bg-slate-100 border border-slate-200 text-xs font-mono font-bold text-slate-800 truncate">
                  {cycleCode}
                </div>
              ) : (
                <select
                  value={selectedCycleId}
                  onChange={(e) => setSelectedCycleId(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-slate-600"
                >
                  <option value="">-- General Farm Overhead (Non-Cycle) --</option>
                  {cycles.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.cycle_code} ({c.growers?.full_name || 'Company-Owned'})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Farm Location (Optional)</label>
              <select
                value={selectedFarmId}
                onChange={(e) => setSelectedFarmId(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-slate-600"
              >
                <option value="">-- Unassigned / Central --</option>
                {farms.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.farm_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Transaction Category (COA) & Expense Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Chart of Accounts Category *</label>
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-slate-600"
                required
              >
                <option value="">-- Select COA Category --</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    [{c.code}] {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Expense Date *</label>
              <Input
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
                className="h-9 text-xs"
                required
              />
            </div>
          </div>

          {/* Description & Amount */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Description / Purpose *</label>
            <Input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Hauling permits for transport to abattoir; rice hull bedding"
              className="h-9 text-xs"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Expense Amount (₱) *</label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="₱0.00"
                className="h-9 text-xs font-mono font-bold text-slate-900"
                required
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Supplier / Payee (Optional)</label>
              <select
                value={selectedSupplierId}
                onChange={(e) => setSelectedSupplierId(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-slate-600"
              >
                <option value="">-- Select Payee / Supplier --</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.supplier_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Reference # & Notes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Receipt / OR / Reference #</label>
              <Input
                type="text"
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
                placeholder="e.g. OR-99214 / Permit #881"
                className="h-9 text-xs font-mono"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Notes / Remarks</label>
              <Input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional internal remarks"
                className="h-9 text-xs"
              />
            </div>
          </div>

          {/* Receipt Attachment */}
          <div className="space-y-1.5 p-3 rounded-lg border border-slate-200 bg-slate-50">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5 text-slate-500" />
              Official Receipt / Voucher Photo (WP07 Secure Storage)
            </label>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-xs font-medium text-slate-700 shadow-xs">
                <Camera className="w-3.5 h-3.5 text-slate-600" />
                <span>{attachedFile ? 'Change Receipt' : 'Take Photo / Upload Receipt'}</span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  capture="environment"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              {attachedFile && (
                <span className="text-xs font-mono text-emerald-700 truncate max-w-[200px]">
                  ✓ {attachedFile.name}
                </span>
              )}
            </div>
          </div>

          <DialogFooter className="pt-2 border-t border-slate-200 flex flex-row items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="bg-slate-900 hover:bg-slate-800 text-white font-semibold"
            >
              {loading ? 'Saving Expense...' : 'Save Expense'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
